import { AddressComponentType, ComponentValue, GooglePlacesPayload, GooglePlaceToAddress } from './types';
import { ADDRESS_COMPONENT_TYPES } from './constants';

/**
 * Converts a Google Places selection payload into our normalized address shape.
 *
 * This function takes the `gmaps.address_components` array returned by Google Places
 * (where each component includes a `long_name`, `short_name`, and a list of `types`)
 * and extracts the specific component types we care about (street number, route,
 * neighborhood, locality, administrative areas, and postal codes).
 *
 * How it works:
 * - Iterates through all `address_components` once and builds a lookup map keyed by
 *   `AddressComponentType` (e.g. "street_number", "route", "postal_code").
 * - For each component, it stores both the `long` and `short` values so callers can
 *   request either representation (e.g. state uses the short value like "CA").
 * - If multiple components contain the same type, the first one encountered wins,
 *   matching the behavior of a `find()` lookup.
 * - Missing components resolve to an empty string ("") to keep downstream consumers
 *   simple and consistent.
 *
 * Output mapping:
 * - `address1`: Concatenation of "street_number" + "route" (trimmed). If either is
 *   missing, this gracefully collapses (e.g. just "Main St" or just "123").
 * - `address2`: "neighborhood" (often empty depending on the location / result).
 * - `city`: "locality".
 * - `county`: "administrative_area_level_2".
 * - `state`: The short value of "administrative_area_level_1" prefixed with "US-"
 *   (e.g. "US-CA"). If no state is present, this becomes an empty string.
 * - `zip`: "postal_code".
 * - `zip4`: "postal_code_suffix".
 * - `latitude` / `longitude`: copied from `payload.location.lat` / `payload.location.lng`.
 *
 * Notes:
 * - This function is formatting/normalization only; it does not validate correctness
 *   of the address or attempt geocoding.
 * - Component availability varies by country and by the specific Places result, so
 *   many fields may legitimately be empty strings.
 */
export const googlePlaceToAddress = (payload: GooglePlacesPayload): GooglePlaceToAddress => {
  const { address_components } = payload.gmaps;
  const { lat: latitude, lng: longitude } = payload.location;

  const componentsByType = address_components.reduce<Partial<Record<AddressComponentType, ComponentValue>>>(
    (acc, component) => {
      const value = {
        long: component.long_name ?? '',
        short: component.short_name ?? '',
      };

      component.types.forEach((type) => {
        if (ADDRESS_COMPONENT_TYPES.includes(type as AddressComponentType) && !acc[type as AddressComponentType]) {
          acc[type as AddressComponentType] = value;
        }
      });

      return acc;
    },
    {}
  );

  const get = (type: AddressComponentType, short = false) =>
    (short ? componentsByType[type]?.short : componentsByType[type]?.long) ?? '';

  const address1 = `${get('street_number')} ${get('route')}`.trim();
  const stateAbbr = get('administrative_area_level_1', true);

  return {
    address1,
    address2: get('neighborhood'),
    city: get('locality'),
    county: get('administrative_area_level_2'),
    state: stateAbbr ? `US-${stateAbbr}` : '',
    zip: get('postal_code'),
    zip4: get('postal_code_suffix'),
    latitude,
    longitude,
  };
};
